import { useLookbookProductItemContext } from '@app/components/products/context/LookbookProductItemContext';
import { LookbookProductBadges } from '@app/components/products/LookbookProductBadges';
import { CustomImage } from '@app/components/various/CustomImage';
import { FavouritesFill, FavouritesOutline } from '@app/components/various/Icons';
import { Spinner } from '@app/components/various/Spinner';
import { getDefaultImageAspectRatio } from '@app/ducks/helpers';
import { GAProductListItemId, GAProductListItemName, mapProductToGAItem, useGTM } from '@app/utils/analytics';
import { cn } from '@app/utils/cn';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { useIsTouchScreen } from '@app/utils/hooks/useIsTouchScreen';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { loadImage } from '@app/utils/loadImage';
import { Picture, Product } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

interface Props {
  image: Picture;
  isActive: boolean;
  variant: Product.Standard;
  isCompact: boolean;
  overlapHeight: number;
  parentWidth: number;
  imageClassName?: string;
  shouldSlideUpOnHover?: boolean;
}

export const LookbookGallerySlide = ({
  image,
  isActive,
  variant,
  isCompact,
  overlapHeight,
  parentWidth,
  imageClassName,
  shouldSlideUpOnHover,
}: Props) => {
  const hasImage = isDefined(image.src) && !isEmpty(image.src);
  const { productsGridSize, productPathName, hasHoverOrFocus, isActiveVariantWishlisted, isReadOnly, toggleProductInWishlist } =
    useLookbookProductItemContext();

  const { trackGTMEvent } = useGTM();
  const handleWishlistToggle = () => {
    if (!isActiveVariantWishlisted) {
      trackGTMEvent('add_to_wishlist', {
        ecommerce: {
          items: [
            mapProductToGAItem(variant, { item_list_id: GAProductListItemId.Lookbook, item_list_name: GAProductListItemName.Lookbook }),
          ],
        },
      });
    }
    toggleProductInWishlist();
  };
  const isLookbookWishlistEnabled = getIsFeatureEnabled('ENABLE_LOOKBOOK_WISHLIST');
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasLoaded, setHasLoaded] = React.useState(!hasImage);
  const isTouchScreen = useIsTouchScreen();
  const imageAspectRatio = useSelector(getDefaultImageAspectRatio);

  React.useEffect(() => {
    if (!isActive || hasLoaded) {
      return;
    }

    setIsLoading(true);

    const handleLoad = () => {
      setIsLoading(false);
      setHasLoaded(true);
      loadImageSubscription.unsubscribe();
    };

    const loadImageSubscription = loadImage(image.src).subscribe({
      complete: handleLoad,
      error: handleLoad,
    });
  }, [hasLoaded, image.src, isActive]);

  const imageSrc = hasLoaded ? image.src : undefined;

  return (
    <div className="group/slide relative w-full flex-shrink-0 flex-grow-0 basis-full snap-center snap-always">
      <div className="absolute right-2 top-2 z-elevated">
        {isLookbookWishlistEnabled && !isReadOnly && (
          <button onClick={handleWishlistToggle} className="rounded-large bg-white p-1">
            {isActiveVariantWishlisted ?
              <FavouritesFill className="size-5" />
            : <FavouritesOutline className="size-5" />}
          </button>
        )}
      </div>
      <Link to={productPathName} tabIndex={-1}>
        {isLoading && (
          <div className="absolute z-elevated flex h-full w-full items-center justify-center opacity-100 transition-opacity">
            <Spinner className="size-8" />
          </div>
        )}
        <CustomImage
          src={imageSrc}
          aspectRatio={imageAspectRatio}
          placeholderClassName={cn('transition-transform', {
            '-translate-y-12': shouldSlideUpOnHover && !isTouchScreen && hasHoverOrFocus,
            'opacity-70': isLoading,
          })}
          errorClassName={cn({ 'opacity-0': hasImage })}
          imageClassName={imageClassName}
        />
      </Link>
      <div
        className={cn('pointer-events-none absolute inset-0 z-elevated w-fit max-w-full', {
          'p-2': productsGridSize === 'small' || productsGridSize === 'medium',
          'p-5': productsGridSize === 'large',
        })}
      >
        <LookbookProductBadges
          badgeSize={isCompact ? 'small' : 'medium'}
          spacing={productsGridSize === 'large' ? 'medium' : 'small'}
          variant={variant}
          overlapHeight={overlapHeight}
          parentWidth={parentWidth}
        />
      </div>
    </div>
  );
};
